<template>
  <el-popover
    v-model="visible"
    placement="bottom-end"
    :width="220"
    trigger="click"
  >
    <div class="cm-header">
      <span class="cm-title">{{ $t('machineUser.columns') }}</span>
      <el-link type="primary" class="cm-reset" @click="resetDefaults">{{ $t('machineUser.resetDefault') }}</el-link>
    </div>
    <ul class="cm-list">
      <li
        v-for="(col, index) in localColumns"
        :key="col.key"
        class="cm-item"
        draggable="true"
        @dragstart="onDragStart(index)"
        @dragover.prevent="onDragOver(index)"
        @drop="onDrop"
        :class="{ 'cm-drag-over': dragOverIndex === index && dragIndex !== index }"
      >
        <span class="cm-handle">⠿</span>
        <el-checkbox
          v-model="col.visible"
          @change="emitChange"
          class="cm-checkbox"
        >{{ col.label }}</el-checkbox>
      </li>
    </ul>
    <el-button
      slot="reference"
      size="small"
      icon="el-icon-setting"
    >{{ $t('machineUser.columns') }}</el-button>
  </el-popover>
</template>

<script>
export default {
  name: 'ColumnManager',
  props: {
    columns: { type: Array, required: true },
    uid: { type: String, default: '' },
    storageKey: { type: String, default: '' }
  },
  data() {
    return {
      visible: false,
      localColumns: [],
      defaults: [],
      dragIndex: -1,
      dragOverIndex: -1
    };
  },
  created() {
    this.defaults = this.columns.map(c => ({ ...c }));
    const saved = this.loadFromStorage();
    if (saved) {
      // merge: apply saved order/visibility, append any new keys not in saved
      const savedKeys = saved.map(c => c.key);
      const merged = saved.filter(s => this.columns.find(c => c.key === s.key));
      this.columns.forEach(c => {
        if (!savedKeys.includes(c.key)) merged.push({ ...c });
      });
      this.localColumns = merged;
      // Push the loaded/merged state up to the parent immediately, so the
      // table's rendered columns/order match the picker on first render
      // instead of only after the user next touches a checkbox or drag handle.
      this.emitChange();
    } else {
      this.localColumns = this.columns.map(c => ({ ...c }));
    }
  },
  methods: {
    onDragStart(index) {
      this.dragIndex = index;
    },
    onDragOver(index) {
      this.dragOverIndex = index;
    },
    onDrop() {
      if (this.dragIndex === -1 || this.dragIndex === this.dragOverIndex) {
        this.dragIndex = -1;
        this.dragOverIndex = -1;
        return;
      }
      const cols = [...this.localColumns];
      const [moved] = cols.splice(this.dragIndex, 1);
      cols.splice(this.dragOverIndex, 0, moved);
      this.localColumns = cols;
      this.dragIndex = -1;
      this.dragOverIndex = -1;
      this.emitChange();
    },
    resetDefaults() {
      this.localColumns = this.defaults.map(c => ({ ...c }));
      this.emitChange();
    },
    emitChange() {
      const updated = this.localColumns.map(c => ({ ...c }));
      this.saveToStorage(updated);
      this.$emit('change', updated);
    },
    getStorageKey() {
      return this.storageKey || `machineUserColumns_v2_${this.uid}`;
    },
    saveToStorage(cols) {
      try {
        localStorage.setItem(this.getStorageKey(), JSON.stringify(cols));
      } catch (e) { /* ignore */ }
    },
    loadFromStorage() {
      try {
        const raw = localStorage.getItem(this.getStorageKey());
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }
  }
};
</script>

<style scoped>
.cm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0 8px;
  border-bottom: 1px solid var(--vtm-line);
  margin-bottom: 4px;
}
.cm-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--vtm-ink);
}
.cm-reset {
  font-size: 12px;
}
.cm-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 380px;
  overflow-y: auto;
}
.cm-item {
  display: flex;
  align-items: center;
  padding: 5px 4px;
  border-radius: 4px;
  cursor: grab;
  user-select: none;
  transition: background 0.15s;
}
.cm-item:hover {
  background: var(--vtm-well);
}
.cm-item.cm-drag-over {
  background: var(--vtm-mint-soft);
  border-top: 2px solid var(--vtm-mint);
}
.cm-handle {
  color: var(--vtm-ink-3);
  margin-right: 8px;
  font-size: 16px;
  cursor: grab;
}
.cm-checkbox {
  flex: 1;
  font-size: 13px;
}
</style>
